/**
 * Bảng lịch tự hồi phục khi mạng chập chờn (đợt 3 · 3.1) — phần thuần, không React.
 *
 * Vì sao (bài học 11/10): wifi quầy rớt vài chục giây giữa giờ đông → chi nhánh chỉ tải MỘT lần, lỗi thì bảng đứng,
 * không nút thử lại, không hẹn giờ; `fetch` không hạn chót nên "đang tải" treo mãi. Lễ tân phải tải lại cả trang.
 *
 * Ranh giới (NOT-DOING #12 — không offline-cache): module này KHÔNG lưu dữ liệu lịch ở đâu cả. Nó chỉ quyết định
 * KHI NÀO hỏi lại máy chủ. Số đang hiện là số của lần tải thành công gần nhất, kèm giờ của lần đó.
 */

/** Hạn chót một yêu cầu đọc: quá thì coi như mất mạng. */
export const GIOI_HAN_GET_MS = 15_000
/** Hạn chót một yêu cầu ghi (POST). Quá thì "chưa chắc đã lưu" — KHÔNG tự gửi lại. */
export const GIOI_HAN_POST_MS = 20_000
/** Tải ảnh / nhập danh sách: thân lớn qua 4G yếu, cần lâu hơn mới công bằng. */
export const GIOI_HAN_TAI_LEN_MS = 60_000
/** Lịch tự tải lại mỗi chừng này khi tab đang hiện. */
export const CHU_KY_TAI_LAI_MS = 60_000

const BAC_THU_LAI_MS = [2_000, 5_000, 15_000] as const
const MOI_LAN_SAU_MS = 30_000

/** Chờ bao lâu trước lần thử lại thứ `lanThu` (bắt đầu từ 0): 2s, 5s, 15s, rồi mỗi 30s. */
export function doTreThuLai(lanThu: number): number {
  return BAC_THU_LAI_MS[lanThu] ?? MOI_LAN_SAU_MS
}

/**
 * Lỗi nào đáng tự thử lại: mất mạng (0), hết giờ (408), quá tải (429), máy chủ lỗi (5xx).
 * Lỗi nghiệp vụ (400/401/403/404/409…) thử lại y nguyên cũng ra y nguyên — chỉ chờ người bấm.
 */
export function nenThuLai(status: number): boolean {
  return status === 0 || status === 408 || status === 429 || status >= 500
}

/** Hẹn giờ tách ra để test được bằng đồng hồ giả (và không dính `window` khi chạy trong Node). */
export interface Hen {
  datGio(f: () => void, ms: number): unknown
  huyGio(gio: unknown): void
  lapLai(f: () => void, ms: number): unknown
  huyLap(gio: unknown): void
}

export const henThat: Hen = {
  datGio: (f, ms) => setTimeout(f, ms),
  huyGio: (gio) => clearTimeout(gio as ReturnType<typeof setTimeout>),
  lapLai: (f, ms) => setInterval(f, ms),
  huyLap: (gio) => clearInterval(gio as ReturnType<typeof setInterval>),
}

export interface BoThuLai {
  /** Hẹn một lần thử lại (đè hẹn cũ). Trả số mili giây sẽ chờ. */
  henLai(f: () => void): number
  /** Có việc mới (người bấm, đổi ngày): bỏ hẹn đang chờ, giữ bậc thang. */
  huyHen(): void
  /** Đã về được: bỏ hẹn, bậc thang về 2 giây. */
  datLai(): void
}

/**
 * `ngauNhien` (0..1) rải ±20% quanh bậc thang để nhiều máy cùng mất mạng một lúc không dồn yêu cầu vào cùng giây khi
 * mạng về. Mặc định 0.5 = đúng bậc (test dùng đồng hồ giả cần con số chính xác); màn Lịch truyền `Math.random`.
 */
export function taoBoThuLai(hen: Hen = henThat, ngauNhien: () => number = () => 0.5): BoThuLai {
  let lan = 0
  let gio: unknown = null
  const huyHen = () => {
    if (gio !== null) hen.huyGio(gio)
    gio = null
  }
  return {
    henLai(f) {
      huyHen()
      const ms = Math.round(doTreThuLai(lan) * (0.8 + 0.4 * ngauNhien()))
      lan += 1
      gio = hen.datGio(() => {
        gio = null
        f()
      }, ms)
      return ms
    },
    huyHen,
    datLai() {
      huyHen()
      lan = 0
    },
  }
}

/** Phần của `window` / `document` mà ta cần — đủ nhỏ để test dựng giả mà không cần trình duyệt. */
export interface NguonSuKien {
  addEventListener(loai: string, nghe: () => void): void
  removeEventListener(loai: string, nghe: () => void): void
}
export interface MoiTruong {
  cuaSo: NguonSuKien
  taiLieu: NguonSuKien & { readonly hidden: boolean }
  hen: Hen
}

/**
 * Tự tải lại khi: có mạng trở lại (`online`), tab hiện lại (`visibilitychange`), và mỗi 60 giây khi tab đang hiện.
 * Trả hàm gỡ. `taiLai` phải "êm": không nháy, không đóng tờ đang mở, không ghi đè bản mới hơn (xem `bo-tai-lich.ts`).
 */
export function batTuTaiLai(taiLai: () => void, mt?: MoiTruong): () => void {
  const m: MoiTruong = mt ?? { cuaSo: window, taiLieu: document, hen: henThat }
  const khiOnline = () => taiLai()
  const khiHien = () => {
    if (!m.taiLieu.hidden) taiLai()
  }
  m.cuaSo.addEventListener('online', khiOnline)
  m.taiLieu.addEventListener('visibilitychange', khiHien)
  const nhip = m.hen.lapLai(() => {
    if (!m.taiLieu.hidden) taiLai()
  }, CHU_KY_TAI_LAI_MS)
  return () => {
    m.cuaSo.removeEventListener('online', khiOnline)
    m.taiLieu.removeEventListener('visibilitychange', khiHien)
    m.hen.huyLap(nhip)
  }
}
